import { useEffect, useState } from 'react';
import type { Promotion } from '@urbanrats/contracts';
import type { ClientGateways } from '../gateway/index.ts';
import { NetworkError } from '../gateway/types.ts';
import { useI18n, type MessageKey } from '../i18n/index.ts';
import { fmtDateTime, fmtMoney, fmtOdds } from '../lib/format.ts';
import { gameTitleKey } from '../lib/gameText.ts';

/** Label of one inventory item (never shown as balance). */
export function rewardLabel(p: Promotion, t: ReturnType<typeof useI18n>['t'], intl: string): string {
  if (p.kind === 'FREEBET' && p.faceValue) return t('rewards.freebet', { amount: fmtMoney(p.faceValue, intl) });
  return t('rewards.boost', { pct: (p.boostBps ?? 0) / 100 });
}

export function useRewards(gw: ClientGateways) {
  const promo = gw.promo;
  const [rev, setRev] = useState(0);
  useEffect(() => promo?.subscribe(() => setRev((n) => n + 1)), [promo]);
  return { promo, rev, items: promo ? promo.items() : [] };
}

/**
 * Inventory, daily check-in, level, Lightning and the promo terms. Feature-flagged by the operator;
 * real-money promotions stay off. No copy that nudges players to chase losses.
 */
export function RewardsPanel({ gw }: { gw: ClientGateways }) {
  const { t, intl } = useI18n();
  const { promo, items } = useRewards(gw);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!promo) return null;
  const f = promo.flags;
  const level = promo.level();
  const checkin = promo.checkinState();
  const lp = promo.lightningPolicy;
  const totalWeight = lp ? lp.tiers.reduce((a, x) => a + x.weight, 0) : 1;

  return (
    <div className="rewards" data-testid="rewards-panel">
      <p className="note">{t(gw.session.moneyMode === 'REAL' ? 'rewards.real' : 'rewards.demo')}</p>

      {f.dailyCheckin && checkin && (
        <section className="rw-box" data-testid="checkin">
          <h3>{t('rewards.checkin')}</h3>
          <p className="note">{t('rewards.checkinStreak', { n: checkin.streakDays })}</p>
          {checkin.canClaim ? (
            <button
              type="button"
              className="confirm"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void promo
                  .checkin()
                  .then((r) => setMsg(r.ok ? (r.granted ? t('rewards.checkinGranted', { item: rewardLabel(r.granted, t, intl) }) : null) : r.error))
                  .catch((e: unknown) => setMsg(e instanceof NetworkError ? t('reason.NETWORK') : String(e)))
                  .finally(() => setBusy(false));
              }}
            >
              {t('rewards.checkinClaim')}
            </button>
          ) : (
            <p role="status">{t('rewards.checkinDone', { t: checkin.nextClaimAt ? fmtDateTime(checkin.nextClaimAt, intl) : '' })}</p>
          )}
          {msg && <p role="status">{msg}</p>}
          <p className="note">{t('rewards.checkinNote')}</p>
        </section>
      )}

      {f.level && level && (
        <section className="rw-box" data-testid="level">
          <h3>{t('rewards.level', { n: level.level })}</h3>
          <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={level.xpForNext} aria-valuenow={level.xp} aria-label={t('rewards.levelXp', { xp: level.xp, next: level.xpForNext })}>
            <div className="progress-fill" style={{ transform: `scaleX(${level.xp / level.xpForNext})` }} />
          </div>
          <p className="note">{t('rewards.levelXp', { xp: level.xp, next: level.xpForNext })}</p>
          <p className="note">{t('rewards.levelNote')}</p>
        </section>
      )}

      {f.inventory && (
        <section className="rw-box">
          <h3>{t('rewards.inventory')}</h3>
          <p className="note">{t('rewards.notMoney')}</p>
          {items.length === 0 ? (
            <p className="muted">{t('rewards.empty')}</p>
          ) : (
            <ul className="inventory" data-testid="inventory">
              {items.map((p) => (
                <li key={p.promotionId} className={`inv-item inv-${p.state}`} data-testid={`inv-${p.promotionId}`} data-state={p.state}>
                  <div className="hi-row">
                    <strong>{rewardLabel(p, t, intl)}</strong>
                    <span className={`status-tag inv-${p.state}`}>{t(`rewards.state.${p.state}` as MessageKey)}</span>
                  </div>
                  <p className="note">
                    {t(`rewards.origin.${p.origin}` as MessageKey)} · {p.expiresAt ? t('rewards.expires', { t: fmtDateTime(p.expiresAt, intl) }) : t('rewards.noExpiry')}
                  </p>
                  {p.freebetPolicy && <p className="note">{t(`rewards.policy.${p.freebetPolicy}` as MessageKey)}</p>}
                  <p className="note">
                    {t('rewards.games', { games: p.games.length >= 5 ? t('rewards.allGames') : p.games.map((g) => t(gameTitleKey(g))).join(', ') })}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {f.lightning && lp && (
        <section className="rw-box" data-testid="lightning-info">
          <h3>⚡ {t('rewards.lightning')}</h3>
          <p className="note">{t('rewards.lightningBody')}</p>
          <table className="rules-table">
            <caption className="sr-only">{t('rewards.lightningCols')}</caption>
            <tbody>
              {lp.tiers.map((x) => (
                <tr key={x.tier}>
                  <th scope="row">{t(`tier.${x.tier}` as MessageKey)}</th>
                  <td className="tabular">{((x.weight / totalWeight) * 100).toLocaleString(intl, { maximumFractionDigits: 2 })}%</td>
                  <td className="tabular">{fmtOdds(x.factor, intl)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!lp.eligibleMarkets.includes('EXACT_SIDE') && <p className="note">{t('rewards.lightningExact', { version: lp.policyVersion })}</p>}
        </section>
      )}

      <section className="rw-box" data-testid="promo-terms">
        <h3>{t('rewards.terms')}</h3>
        <p>{t('rewards.cap', { cap: fmtMoney(promo.policy.payoutCap, intl) })}</p>
        <p className="note">{t('rewards.onePerBet', { pct: promo.policy.maxBoostBpsPerBet / 100 })}</p>
        {!promo.policy.freebetWithLightning && <p className="note">{t('rewards.stackFreebetLightning')}</p>}
        {promo.policy.boostWithLightning && <p className="note">{t('rewards.stackBoostLightning')}</p>}
        <p className="note">{t('rewards.voidPolicy')}</p>
        <p className="note mono">{t('rewards.formula', { version: promo.policy.policyVersion, formula: promo.policy.formula })}</p>
      </section>
    </div>
  );
}
